import React, { useEffect } from "react";
import { Link } from "react-router-dom";

import { ErrorBoundary } from "traec-react/errors";
import { MiniErrorBoundary } from "AppSrc/project/report/error";

import { getProjectPermissions } from "traec/utils/permissions/project";
import { useTreeContext } from "./context";
import { Indent } from "./indent";
import Im from "immutable";
import { Hide } from "storybook-dashboard/utils";
import { ProjectPermission } from "../project/utils/permissions";
import { WPDropDownMenu } from "./wpRow";
import { useIsCurrent } from "./utils";
import { useLocalStorage } from "AppSrc/utils/hooks";

function SubRefs({ childPaths, refsByPath, depth }) {
  const { searchTerm, isMatchingItem, hasMatchingDescendant, isDescendantOfMatching } = useTreeContext();

  const shouldShowPath = (path) => {
    if (!searchTerm) return true;
    const ref = refsByPath.get(path);
    return (
      isMatchingItem(ref, searchTerm) ||
      hasMatchingDescendant({ refPath: path }) ||
      isDescendantOfMatching(ref, "ref", ref?.get("uid"))
    );
  };

  return (childPaths || Im.List())
    .filter((path) => shouldShowPath(path))
    .map((path, i) => <WorkPackageRow key={i} path={path} depth={depth} refsByPath={refsByPath} />);
}

function WorkPackageRow({ depth = 0, path, refsByPath, includeRoot = false }) {
  const isRoot = path?.length === 7;
  const ref = refsByPath.get(path);
  const refId = ref?.get("uid");

  const [expanded, setExpanded] = useLocalStorage(`sidebar-${refId}`);
  const { searchTerm, isMatchingItem, hasMatchingDescendant, isDescendantOfMatching, shouldExpandWorkPackage } =
    useTreeContext();
  const isSelected = useIsCurrent({ id: refId });

  // Auto-expand if selected
  useEffect(() => {
    if (isSelected && !expanded) setExpanded(true);
  }, [isSelected, expanded, setExpanded]);

  // Avoid recomputing
  const _hasMatchingDescendant = hasMatchingDescendant({ refPath: path });

  const shouldAutoExpand = searchTerm && _hasMatchingDescendant;

  // Auto-expand if this work package contains the selected ref as a descendant
  const shouldExpandForSelection = shouldExpandWorkPackage && shouldExpandWorkPackage(path);

  const isExpanded = expanded === true || shouldAutoExpand || shouldExpandForSelection;

  // Hide if doesn't match search, has no matching descendants, AND is not a descendant of a matching item
  const shouldHide =
    searchTerm &&
    !isMatchingItem(ref, searchTerm) &&
    !_hasMatchingDescendant &&
    !isDescendantOfMatching(ref, "ref", refId);

  const bgColor = isSelected ? "bg-info" : "";
  const styleObj = { display: "inline-block", verticalAlign: "middle" };

  const projectId = ref?.get("project");
  const _projectId = projectId?.substring(0, 8);
  const _refId = ref?.get("uid")?.substring(0, 8);
  const name = ref?.get("name") || "<no name>";
  let linkLoc = isRoot ? `/project/${_projectId}` : `/project/${_projectId}/wpack/${_refId}/report`;

  const hide = (isRoot && !includeRoot) || shouldHide;

  const isLeaf = (ref?.get("childPaths") || Im.List()).size === 0;
  console.log("WorkPackageRow", path, ref?.toJS());

  return (
    <ErrorBoundary>
      <Hide when={hide}>
        <tr className={`${bgColor}`} style={{ minHeight: "1.5rem" }}>
          <td className="m-0 p-0" style={{ verticalAlign: "middle", border: "none", width: "100%", height: "100%" }}>
            <div style={{ display: "flex", alignItems: "stretch", height: "100%", minHeight: "1.5rem" }}>
              <Indent
                depth={depth}
                expanded={isExpanded}
                onClickHandler={(e) => {
                  // Don't allow collapsing if a sub-ref is selected (unless we're expanding)
                  if (shouldExpandForSelection && expanded) {
                    return; // Prevent collapsing
                  }
                  setExpanded(!expanded);
                }}
                disabled={isLeaf}
              />
              <div style={{ display: "flex", alignItems: "center" }}>
                <Link to={linkLoc} className="ml-2" style={styleObj}>
                  <i>{name}</i>
                </Link>
                <Hide when={!isSelected}>
                  <ProjectPermission projectId={projectId} requiresAdmin={true}>
                    <WPDropDownMenu _ref={ref} />
                  </ProjectPermission>
                </Hide>
              </div>
            </div>
          </td>

          {/* Empty cell to maintain table structure for ExtraContentComponent column */}
          <td className="m-0 p-1" style={{ border: "none" }}></td>

          {/* Empty cell for admin actions column */}
          <td className="m-0 p-1" style={{ border: "none", width: "5%" }}></td>
        </tr>
      </Hide>

      <Hide when={!isExpanded && !hide}>
        <SubRefs childPaths={ref.get("childPaths")} refsByPath={refsByPath} depth={hide ? depth : depth + 1} />
      </Hide>
    </ErrorBoundary>
  );
}

export default function ProjectWPTree({ projectId, depth = 0 }) {
  const { refsByProjectId, excludeLevels } = useTreeContext();

  if (excludeLevels?.has("ReportingPackage")) return null;

  const refs = refsByProjectId?.get(projectId);

  // Create a map of refs by their path
  // Each ref should have a childPaths list of paths that are its children
  // The root path is the shortest path (7 characters)
  let refsByPath = (refs || Im.List()).reduce((acc, ref) => {
    const path = ref.get("path");
    const parentPath = path.slice(0, -7);
    return acc
      .update(path, Im.Map(), (existing) => existing.merge(ref))
      .updateIn([parentPath, "childPaths"], Im.List(), (childPaths) => childPaths.push(path));
  }, Im.Map());

  const rootPath = refsByPath.keySeq().sort().first()?.substring(0, 7);

  if (!refsByPath || refsByPath.isEmpty() || !rootPath) {
    return null;
  }

  return (
    <WorkPackageRow
      path={rootPath}
      depth={depth + 1}
      projectId={projectId}
      showMenu={true}
      includeRoot={false}
      alternateBgColor={false}
      menuOnSelectedOnly={true}
      refsByPath={refsByPath}
      //userPermissions={userPermissions}
    />
  );
}
